import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, ChevronRight, Shield, ShieldAlert, ShieldOff } from "lucide-react";
import Flag from "lucide-react/dist/esm/icons/flag";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import { PageHeader, PrimaryButton, Card } from "../components/UI";
import StatusPill from "../components/StatusPill";
import BrandBadge from "../components/BrandBadge";
import { useAuth } from "../contexts/AuthContext";

function CountBadge({ n, tone }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-full px-2"
      style={{
        fontSize: 11, fontWeight: 600, minWidth: 22, height: 20,
        color: tone === "blind" ? "#B45309" : tone === "review" ? "#0F766E" : "#3B3A36",
        background: tone === "blind" ? "#FEF3E2" : tone === "review" ? "#D3EBE7" : "#EEECE6",
      }}
    >
      {n}
    </span>
  );
}

/* Item 4 — insurance expiry / missing card. Renders ONLY when status is
 * not "ok" (see Dashboard render, above). Warning token (amber) for
 * expiring_soon; note-red for expired and missing — same card, different
 * copy. Uses existing tailwind tokens; no new colours. */
function InsuranceCard({ insurance, navigate }) {
  const status = insurance.status;
  const days = insurance.days_until_expiry;
  const hasCert = Boolean(insurance.has_certificate);
  const isExpired = status === "expired";
  const isMissing = status === "missing";
  const isRed = isExpired || isMissing;
  const bg = isRed ? "#FEE2E2" : "#FEF3E2";
  const fg = isRed ? "#B91C1C" : "#B45309";
  const Icon = isRed ? ShieldOff : ShieldAlert;
  const daysAbs = typeof days === "number" ? Math.abs(days) : null;
  // The `missing` status covers two distinct situations — the copy must
  // match the actual gap so users know what to do.
  const title =
    isMissing
      ? (hasCert
          ? "Certificate on file has no valid expiry date"
          : "No insurance certificate on file")
    : isExpired
      ? `Insurance certificate expired ${daysAbs} day${daysAbs === 1 ? "" : "s"} ago`
    : `Insurance certificate expires in ${daysAbs} day${daysAbs === 1 ? "" : "s"}`;
  const body =
    isMissing
      ? (hasCert
          ? "Add the expiry date in Settings \u2192 Insurance so renewal can be tracked."
          : "Upload the current PDF before sending client documents.")
    : isExpired
      ? "Cover may have lapsed. Client documents may reference an out-of-date certificate."
    : "Renew before the expiry to keep client documents current.";
  return (
    <div className="mb-8" data-testid="dashboard-insurance-card">
      <div
        className="flex items-center justify-between gap-4 rounded-lg px-4 py-3 border"
        style={{ background: bg, borderColor: fg + "40", color: fg }}
      >
        <div className="flex items-center gap-3">
          <Icon size={20} />
          <div className="flex flex-col gap-0.5">
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</span>
            <span className="text-ink-2" style={{ fontSize: 12 }}>{body}</span>
          </div>
        </div>
        <button
          onClick={() => navigate("/settings?tab=insurance")}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md hover:opacity-80"
          style={{ background: fg, color: "#FFF", fontSize: 12, fontWeight: 600 }}
          data-testid="insurance-open-settings-btn"
        >
          Open settings <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

function FlaggedCard({ navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/flagged-items").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const blind = data?.blind || [];
  const other = data?.other || [];
  const total = data?.total || 0;
  // Merge for the capped preview, keeping oldest-first ordering across both.
  const merged = [...blind, ...other].sort((a, b) => (a.flagged_at || "").localeCompare(b.flagged_at || ""));
  const visible = merged.slice(0, 8);

  const open = (row) => navigate(`/tenders/${row.tender_id}?tab=estimate&line=${row.line_id}`);

  return (
    <div className="mb-8" data-testid="dashboard-flagged-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <h2 className="font-display text-ink inline-flex items-center gap-2" style={{ fontSize: 16, fontWeight: 600 }}>
            <Flag size={15} className="text-ink-3" /> Flagged for review
          </h2>
          <CountBadge n={total} tone="default" />
          {(data?.blind_count || 0) > 0 && (
            <span className="text-ink-3" style={{ fontSize: 11.5 }}>
              incl. <span style={{ color: "#B45309", fontWeight: 600 }}>{data.blind_count} assumed</span>
            </span>
          )}
        </div>
        {total > 0 && (
          <button
            onClick={() => navigate("/flagged")}
            className="text-ink-3 hover:text-ink inline-flex items-center gap-1"
            style={{ fontSize: 12 }}
            data-testid="flagged-view-all"
          >
            View all <ArrowRight size={12} />
          </button>
        )}
      </div>

      <Card>
        {total === 0 ? (
          <div className="py-10 text-center">
            <p className="text-ink-2" style={{ fontSize: 13, fontWeight: 500 }}>Nothing flagged</p>
            <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
              Flag a line as blind or for review from a tender estimate and it will appear here.
            </p>
          </div>
        ) : (
          <div>
            <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-line-soft">
              <span className="col-head">Assumed items</span>
              <CountBadge n={data?.blind_count || 0} tone="blind" />
              <span className="col-head ml-4">Other flagged</span>
              <CountBadge n={data?.other_count || 0} tone="review" />
            </div>
            <table className="w-full text-left">
              <tbody>
                {visible.map((r, i) => (
                  <tr
                    key={r.line_id}
                    onClick={() => open(r)}
                    data-testid={`dashboard-flagged-row-${r.line_id}`}
                    className={["group cursor-pointer hover:bg-surface-2/60", i !== visible.length - 1 ? "border-b border-line-soft" : ""].join(" ")}
                  >
                    <td className="px-4 py-2.5" style={{ width: 90 }}>
                      <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>{r.tender_ref || "—"}</span>
                    </td>
                    <td className="px-4 py-2.5" style={{ width: 160 }}>
                      <span className="text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{r.client_name || "—"}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Flag size={11} style={{ color: r.flag_type === "blind" ? "#B45309" : "#0F766E", flex: "0 0 auto" }} />
                        <span className="text-ink-2 truncate" style={{ fontSize: 12.5, maxWidth: 260 }}>{r.description}</span>
                        {r.flag_reason && (
                          <span className="text-ink-4 italic truncate" style={{ fontSize: 11.5, maxWidth: 200 }}>— {r.flag_reason}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right" style={{ width: 100 }}>
                      <span className="font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>{r.flagged_at ? ukDate(r.flagged_at) : "—"}</span>
                    </td>
                    <td className="pr-4 py-2.5 text-right" style={{ width: 28 }}>
                      <ChevronRight size={13} className="text-ink-4 group-hover:text-ink-2" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function KpiTile({ label, value, hint, accent }) {
  return (
    <div className="bg-surface border border-line rounded-lg p-5 shadow-soft">
      <span className="col-head">{label}</span>
      <div
        className="font-mono tnum mt-2"
        style={{ fontSize: 22, fontWeight: 700, color: accent ? "#2F4F3E" : "#1A1A1A", letterSpacing: "-0.01em" }}
      >
        {value}
      </div>
      {hint && <p className="text-ink-3 mt-1" style={{ fontSize: 11.5 }}>{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary")
      .then((r) => setData(r.data))
      .catch(() => setData({ counts: {}, pipeline_total: 0, won_total: 0, recent: [] }))
      .finally(() => setLoading(false));
  }, []);

  const counts = data?.counts || {};
  const recent = data?.recent || [];
  const total  = (counts.draft||0) + (counts.active||0) + (counts.submitted||0)
               + (counts.won||0)   + (counts.lost||0)   + (counts.archived||0);
  const activeAndSubmitted = (counts.active || 0) + (counts.submitted || 0);

  return (
    <div className="app-container py-8">
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle="Snapshot of your pipeline across both brands."
        actions={
          <PrimaryButton icon={Plus} onClick={() => navigate("/tenders/new")}>
            Create new tender
          </PrimaryButton>
        }
      />

      <div className="grid grid-cols-4 gap-4 mb-8">
        <KpiTile
          label="Active pipeline"
          value={loading ? "—" : gbp(data?.pipeline_total || 0)}
          hint={`${activeAndSubmitted} live tender${activeAndSubmitted === 1 ? "" : "s"}`}
          accent
        />
        <KpiTile
          label="Tenders — active"
          value={loading ? "—" : (counts.active || 0)}
          hint={`${total} total in workspace`}
        />
        <KpiTile
          label="Tenders — submitted"
          value={loading ? "—" : (counts.submitted || 0)}
          hint="Awaiting client decision"
        />
        <KpiTile
          label="Won this year"
          value={loading ? "—" : gbp(data?.won_total || 0)}
          hint={`${counts.won || 0} contract${counts.won === 1 ? "" : "s"} secured`}
        />
      </div>

      {/* Phase 0 — flagged items across active tenders (below the metrics row). */}
      <FlaggedCard navigate={navigate} />

      {/* Item 4 — insurance expiry warning. Only renders when status !== "ok". */}
      {data?.insurance && data.insurance.status !== "ok" ? (
        <InsuranceCard insurance={data.insurance} navigate={navigate} />
      ) : null}

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
          Recent tenders
        </h2>
        <button
          onClick={() => navigate("/tenders")}
          className="text-ink-3 hover:text-ink inline-flex items-center gap-1"
          style={{ fontSize: 12 }}
        >
          View all <ArrowRight size={12} />
        </button>
      </div>

      <Card>
        {recent.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-ink-2" style={{ fontSize: 13.5, fontWeight: 500 }}>
              No tenders yet
            </p>
            <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
              Create your first tender to get started.
            </p>
            <div className="mt-4 flex justify-center">
              <PrimaryButton icon={Plus} onClick={() => navigate("/tenders/new")}>
                Create new tender
              </PrimaryButton>
            </div>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                <th className="col-head px-4 py-2.5" style={{ width: 80 }}>Ref</th>
                <th className="col-head px-4 py-2.5">Client</th>
                <th className="col-head px-4 py-2.5" style={{ width: 70 }}>Brand</th>
                <th className="col-head px-4 py-2.5">Job type</th>
                <th className="col-head px-4 py-2.5" style={{ width: 110 }}>Status</th>
                <th className="col-head px-4 py-2.5" style={{ width: 110 }}>Updated</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {recent.map((t, i) => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/tenders/${t.id}`)}
                  className={[
                    "group cursor-pointer hover:bg-surface-2/60",
                    i !== recent.length - 1 ? "border-b border-line-soft" : ""
                  ].join(" ")}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono tnum text-ink" style={{ fontSize: 12.5 }}>{t.ref}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{t.client_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <BrandBadge brand={t.brand === "lux_construction" ? "CON" : "DEV"} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-ink-2" style={{ fontSize: 12.5 }}>{t.job_type || "—"}</span>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3">
                    <span className="font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{ukDate(t.updated_at)}</span>
                  </td>
                  <td className="pr-4 py-3 text-right">
                    <ChevronRight size={14} className="text-ink-4 group-hover:text-ink-2" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
